import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Chip, Typography, TableContainer, Table, TableBody, TextField, TableHead, TableRow, TableCell } from '@mui/material';
import { TableLoader, Icon, InputWithIcon, AccessPortal, ExpandedTooltip, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'src/stores';
import { AdminAccountMenu } from './AdminAccountMenu';
import { EditAdminAccountModal } from './EditAdminAccountModal';
import { SimpleConfirmationModal } from 'src/components/shared/SimpleConfirmationModal';
import useStyles from './styles';
import warningImg from 'src/img/warning.svg';
import { USER_ROLES_TO_NAME } from 'src/constants/userSetting';
import { mfaChipVariant, mfaLabelKey } from './mfaLabel';
import { adminStatusChipVariant, adminStatusLabelKey, isAdminBlocked } from './accountStatus';
const renderMfaCell = (mfaEnabled, t) => {
  const key = mfaLabelKey(mfaEnabled);
  const variant = mfaChipVariant(mfaEnabled);
  return key && variant ? /*#__PURE__*/React.createElement(Chip, {
    label: t(key),
    variant: variant
  }) : '—';
};
const renderStatusCell = (isEnabled, t) => /*#__PURE__*/React.createElement(Chip, {
  label: t(adminStatusLabelKey(isEnabled)),
  variant: adminStatusChipVariant(isEnabled)
});
export const AdminAccountsGrid = observer(_ref => {
  let externalTriggerRefresh = _ref.externalTriggerRefresh;
  const store = useStore();
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchKey = _useState2[0],
    setSearchKey = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLoading = _useState4[0],
    setIsLoading = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    triggerRefresh = _useState6[0],
    setTriggerRefresh = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    adminAccountsData = _useState8[0],
    setAdminAccountsData = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    triggerEditAdminAccountModal = _useState0[0],
    setTriggerEditAdminAccountModal = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    removeAdminAccountModalState = _useState10[0],
    setRemoveAdminAccountModalState = _useState10[1];
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    blockAdminAccountModalState = _useState12[0],
    setBlockAdminAccountModalState = _useState12[1];
  const _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    modalAdminAccountData = _useState14[0],
    setModalAdminAccountData = _useState14[1];
  const fetchAdminAccountsList = async () => {
    const adminsListResp = await store.admin.getAdminAccountsAsync.run();
    setAdminAccountsData(adminsListResp === null || adminsListResp === void 0 ? void 0 : adminsListResp.response);
  };
  const fetchData = async () => {
    setIsLoading(true);
    await fetchAdminAccountsList();
    setIsLoading(false);
  };
  const handleResendInvite = async accountId => {
    const reInviteUserResp = await store.account.reInviteUserAsync.run(accountId);
    if (!reInviteUserResp || !reInviteUserResp.isSuccessful) return;
    toast('success', {
      body: ['Re-invitation successfully sent']
    });
  };
  const removeAdminAccountActionHandler = async _ref2 => {
    let action = _ref2.action,
      objectToPassOver = _ref2.objectToPassOver;
    setRemoveAdminAccountModalState(false);
    if (action === 'submit') {
      const deleteAdminResp = await store.admin.deleteAdminAccountAsync.run({
        accountId: objectToPassOver.id
      });
      if (deleteAdminResp && deleteAdminResp !== null && deleteAdminResp !== void 0 && deleteAdminResp.isSuccessful) {
        toast('success', {
          body: ['Successfully removed']
        });
        setTriggerRefresh(!triggerRefresh);
      }
    }
  };
  const blockAdminAccountActionHandler = async _ref3 => {
    let action = _ref3.action,
      objectToPassOver = _ref3.objectToPassOver;
    setBlockAdminAccountModalState(false);
    if (action !== 'submit') return;
    const blocked = isAdminBlocked(objectToPassOver.isEnabled);
    const request = blocked ? store.admin.unblockAdminAccountAsync : store.admin.blockAdminAccountAsync;
    const resp = await request.run({
      accountId: objectToPassOver.id
    });
    if (resp && resp !== null && resp !== void 0 && resp.isSuccessful) {
      toast('success', {
        body: [t(blocked ? 'admin_page.successfully_unblocked' : 'admin_page.successfully_blocked')]
      });
      setTriggerRefresh(!triggerRefresh);
    }
  };
  const modalAccountIsBlocked = isAdminBlocked(modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.isEnabled);
  useEffect(() => {
    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRefresh, externalTriggerRefresh]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.filtersWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.searchWrapper
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    id: "company-search",
    label: t('general.search'),
    variant: "outlined",
    value: searchKey,
    onChange: event => {
      var _event$target;
      return setSearchKey((_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);
    },
    fullWidth: true
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.tableWrapper
  }, isLoading ? /*#__PURE__*/React.createElement(TableLoader, null) : !!adminAccountsData && !!(adminAccountsData !== null && adminAccountsData !== void 0 && adminAccountsData.length) && !!adminAccountsData.filter(tm => {
    const searchPhrase = new RegExp(searchKey, 'gi');
    return searchKey.length > 0 ? " ".concat(tm.email).search(searchPhrase) > 0 || " ".concat(tm.newEmail).search(searchPhrase) > 0 || " ".concat(tm.firstName, " ").concat(tm.lastName).search(searchPhrase) > 0 : true;
  }).length ? /*#__PURE__*/React.createElement(TableContainer, null, /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
    width: '320px',
    align: "left"
  }, t('team_page.name')), /*#__PURE__*/React.createElement(TableCell, {
    width: '350px',
    align: "left"
  }, t('general.email')), /*#__PURE__*/React.createElement(TableCell, {
    width: '220px',
    align: "left"
  }, t('general.phone')), /*#__PURE__*/React.createElement(TableCell, {
    width: '120px',
    align: "left"
  }, t('team_page.mfa')), /*#__PURE__*/React.createElement(TableCell, {
    width: '220px',
    align: "left"
  }, t('team_page.role')), /*#__PURE__*/React.createElement(TableCell, {
    width: '120px',
    align: "left"
  }, t('admin_page.status')), /*#__PURE__*/React.createElement(TableCell, {
    width: '120px',
    align: "left"
  }, t('general.actions')))), /*#__PURE__*/React.createElement(TableBody, null, !!adminAccountsData && !!(adminAccountsData !== null && adminAccountsData !== void 0 && adminAccountsData.length) && adminAccountsData.filter(tm => {
    const searchPhrase = new RegExp(searchKey, 'gi');
    return searchKey.length > 0 ? " ".concat(tm.email).search(searchPhrase) > 0 || " ".concat(tm.newEmail).search(searchPhrase) > 0 || " ".concat(tm.firstName, " ").concat(tm.lastName).search(searchPhrase) > 0 : true;
  }).map(adminUser => /*#__PURE__*/React.createElement(TableRow, {
    key: adminUser.id
  }, /*#__PURE__*/React.createElement(TableCell, {
    align: "left"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.tableFullNameCell
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.tableFullNameText
  }, "".concat(adminUser.firstName, " ").concat(adminUser.lastName)), !!adminUser.emailConfirmed ? /*#__PURE__*/React.createElement("div", {
    className: classes.tableFullNameAccess
  }, /*#__PURE__*/React.createElement(AccessPortal, {
    tooltipText: t('team_page.has_access_to_portal'),
    type: "success"
  })) : /*#__PURE__*/React.createElement("div", {
    className: classes.tableFullNameAccess,
    onClick: () => {
      handleResendInvite(adminUser.id);
    }
  }, /*#__PURE__*/React.createElement(AccessPortal, {
    tooltipText: t('team_page.pending_invite'),
    type: "warning"
  })))), /*#__PURE__*/React.createElement(TableCell, {
    align: "left"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.tableEmailCell
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    title: adminUser.email || adminUser.newEmail || 'N/A',
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", null, adminUser.email || adminUser.newEmail || 'N/A')))), /*#__PURE__*/React.createElement(TableCell, {
    align: "left"
  }, adminUser.phone), /*#__PURE__*/React.createElement(TableCell, {
    align: "left"
  }, renderMfaCell(adminUser.mfaEnabled, t)), /*#__PURE__*/React.createElement(TableCell, {
    align: "left"
  }, USER_ROLES_TO_NAME[adminUser.userRole]), /*#__PURE__*/React.createElement(TableCell, {
    align: "left"
  }, renderStatusCell(adminUser.isEnabled, t)), /*#__PURE__*/React.createElement(TableCell, {
    align: "left"
  }, /*#__PURE__*/React.createElement(AdminAccountMenu, {
    key: 'AdminAccountMenuKey_' + adminUser.id,
    selectedAccount: adminUser,
    menuTriggerExternal: /*#__PURE__*/React.createElement(Icon, {
      type: "dotdotdot"
    }),
    handleShowEditAdminAccountModal: setTriggerEditAdminAccountModal,
    handleShowDeleteAdminAccountModal: () => setRemoveAdminAccountModalState(true),
    handleShowBlockAdminAccountModal: adminUser.id === store.account.userProfile.id ? undefined : () => setBlockAdminAccountModalState(true),
    setModalAdminAccountData: setModalAdminAccountData
  }))))))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.xsSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    style: {
      textAlign: 'center'
    }
  }, t('team_page.no_team_members_found'))), /*#__PURE__*/React.createElement("div", {
    className: classes.xsSection
  }, /*#__PURE__*/React.createElement("img", {
    alt: "team members missing data",
    src: warningImg,
    height: '300px',
    width: '100%'
  })))), /*#__PURE__*/React.createElement(EditAdminAccountModal, {
    customTrigger: null,
    data: modalAdminAccountData,
    triggerRefresh: () => setTriggerRefresh(!triggerRefresh),
    handleTriggerShowModal: triggerEditAdminAccountModal,
    handleShowModal: value => setTriggerEditAdminAccountModal(value)
  }), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: t('admin_page.delete_admin_account'),
    subTitleText: (modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.email) || (modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.newEmail),
    bodyText: t('admin_page.you_are_about_to_remove', {
      firstName: modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.firstName,
      lastName: modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.lastName
    }),
    externalIsOpen: removeAdminAccountModalState,
    objectToPassOver: modalAdminAccountData,
    callBack: removeAdminAccountActionHandler,
    customTrigger: null
  }), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: t(modalAccountIsBlocked ? 'admin_page.unblock_admin_account' : 'admin_page.block_admin_account'),
    subTitleText: (modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.email) || (modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.newEmail),
    bodyText: t(modalAccountIsBlocked ? 'admin_page.you_are_about_to_unblock' : 'admin_page.you_are_about_to_block', {
      firstName: modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.firstName,
      lastName: modalAdminAccountData === null || modalAdminAccountData === void 0 ? void 0 : modalAdminAccountData.lastName
    }),
    customSubmitButtonName: t(modalAccountIsBlocked ? 'admin_page.unblock' : 'admin_page.block'),
    externalIsOpen: blockAdminAccountModalState,
    objectToPassOver: modalAdminAccountData,
    callBack: blockAdminAccountActionHandler,
    customTrigger: null
  }));
});